import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { exportAsCSV } from '../../utils/CSVExporter';
import {
    createCustomerDataReportPdfBlob,
    createTransactionDetailPdfBlob,
    exportCustomerDataReportExcel,
    exportTransactionDetailCsv,
    exportTransactionDetailExcel,
} from '../../utils/customerDataReportExport';
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { Button } from 'reactstrap';
import Select from 'react-select';
import { DatePicker } from 'antd';
import moment from 'moment';
import Swal from 'sweetalert2';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllSalesRepresentatives } from '../../services/SalesRepresentativeServices';
import { getCustomerDataReport, getCustomerDataReportExport, getCustomerDataTransactionDetails } from '../../services/ReportServices';
import { displayError } from '../../helpers';
import { HandleSelect, reportMultiSelectComponents, reportFilterSelectStyles } from '../../components/MultiSelectControls';
import ReportExportDropdown from '../../components/ReportExportDropdown';
import { useReportTableExports } from '../../hooks/useReportTableExports';
import { getHTMLForTablePopup } from '../../utils/PopupUtils';
import { openPdfInApp } from '../../utils/openPdfInApp';
import { buildCustomerLedgerPdfHeader } from '../../utils/customerLedgerPdf';

const { RangePicker } = DatePicker;

const filterSelectStyles = reportFilterSelectStyles;

const clickableCellStyle = () => ({
    color: '#2563eb',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontWeight: 600,
});

function formatAmount(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return '0.00';
    }
    return number.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatQty(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return '0';
    }
    return Number.isInteger(number) ? String(number) : number.toFixed(3).replace(/\.?0+$/, '');
}

function sanitizeFilenamePart(value) {
    return String(value || '')
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 40) || 'details';
}

function showTransactionPopup({
    title,
    subtitle,
    columns,
    details,
    footerRow,
    emptyMessage,
    width = '960px',
    exportContext = null,
}) {
    const downloadBar = exportContext ? `
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-bottom:12px;flex-wrap:wrap;">
            <button type="button" id="tx-popup-export-excel" style="padding:6px 14px;border:1px solid #198754;background:#198754;color:#fff;border-radius:6px;font-size:13px;cursor:pointer;">
                Excel
            </button>
            <button type="button" id="tx-popup-export-pdf" style="padding:6px 14px;border:1px solid #dc3545;background:#dc3545;color:#fff;border-radius:6px;font-size:13px;cursor:pointer;">
                PDF
            </button>
            <button type="button" id="tx-popup-export-csv" style="padding:6px 14px;border:1px solid #6c757d;background:#6c757d;color:#fff;border-radius:6px;font-size:13px;cursor:pointer;">
                CSV
            </button>
        </div>
    ` : '';

    Swal.fire({
        title,
        html: `${downloadBar}${getHTMLForTablePopup(subtitle, columns, details, footerRow, emptyMessage)}`,
        width,
        confirmButtonText: 'Close',
        didOpen: () => {
            if (!exportContext) {
                return;
            }
            const payload = {
                title,
                subtitle,
                columns,
                records: details,
                footerRow,
                periodFrom: exportContext.periodFrom,
                periodTo: exportContext.periodTo,
                generatedAt: exportContext.generatedAt,
                filename: exportContext.filenameBase,
            };

            document.getElementById('tx-popup-export-excel')?.addEventListener('click', () => {
                exportTransactionDetailExcel({
                    ...payload,
                    filename: `${exportContext.filenameBase}.xlsx`,
                });
            });

            document.getElementById('tx-popup-export-csv')?.addEventListener('click', () => {
                exportTransactionDetailCsv({
                    columns,
                    records: details,
                    footerRow,
                    filename: `${exportContext.filenameBase}.csv`,
                });
            });

            document.getElementById('tx-popup-export-pdf')?.addEventListener('click', async () => {
                try {
                    Swal.fire({
                        title: 'Preparing PDF...',
                        allowOutsideClick: false,
                        didOpen: () => Swal.showLoading(),
                    });
                    const header = exportContext.cookies
                        ? await buildCustomerLedgerPdfHeader(exportContext.cookies, [], [], {
                            documentTitle: title,
                            subtitle,
                        })
                        : null;
                    const blob = createTransactionDetailPdfBlob({ ...payload, header });
                    Swal.close();
                    openPdfInApp(blob, {
                        title,
                        downloadFileName: `${exportContext.filenameBase}.pdf`,
                    });
                } catch (error) {
                    Swal.close();
                    displayError({ message: error?.message || 'PDF export failed.' }, 'Export Failed');
                }
            });
        },
    });
}

const TRANSACTION_POPUP_CONFIG = {
    quotation: {
        title: 'Quotation Details',
        popupWidth: '1280px',
        emptyMessage: 'No quotations found for this customer/site in the selected period.',
        columns: [
            { key: 'quotation_no', label: 'Quotation No' },
            { key: 'quotation_date', label: 'Date' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'site_name', label: 'Site' },
            { key: 'grade', label: 'Grade' },
            { key: 'qty', label: 'Qty', align: 'right' },
            { key: 'rate', label: 'Rate', align: 'right' },
            { key: 'amount', label: 'Amount', align: 'right' },
            { key: 'delivery_mode', label: 'Mode of Delivery' },
            { key: 'distance', label: 'Distance', align: 'right' },
            { key: 'marketing_name', label: 'Marketing' },
        ],
        mapDetails: (rawDetails) => rawDetails.map((item) => ({
            ...item,
            qty: formatQty(item.qty),
            rate: formatAmount(item.rate),
            amount: formatAmount(item.amount),
            distance: item.distance === '' || item.distance === null || item.distance === undefined
                ? ''
                : formatQty(item.distance),
        })),
        footerRow: (rawDetails) => ({
            quotation_no: 'TOTAL',
            quotation_date: '',
            customer_name: '',
            site_name: '',
            grade: '',
            qty: formatQty(rawDetails.reduce((sum, item) => sum + (Number(item.qty) || 0), 0)),
            rate: '',
            amount: formatAmount(rawDetails.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)),
            delivery_mode: '',
            distance: '',
            marketing_name: '',
        }),
    },
    sales_order: {
        title: 'Sales Order Details',
        popupWidth: '1200px',
        emptyMessage: 'No sales orders found for this customer/site in the selected period.',
        columns: [
            { key: 'sales_order_no', label: 'Sales Order No' },
            { key: 'sales_order_date', label: 'Order Date' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'site_name', label: 'Site' },
            { key: 'valid_upto', label: 'Valid Upto' },
            { key: 'transport_mode', label: 'Mode of Delivery' },
            { key: 'amount', label: 'Amount', align: 'right' },
            { key: 'marketing_name', label: 'Marketing' },
        ],
        mapDetails: (rawDetails) => rawDetails.map((item) => ({
            ...item,
            amount: formatAmount(item.amount),
        })),
        footerRow: (rawDetails) => ({
            sales_order_no: 'TOTAL',
            sales_order_date: '',
            customer_name: '',
            site_name: '',
            valid_upto: '',
            transport_mode: '',
            amount: formatAmount(rawDetails.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)),
            marketing_name: '',
        }),
    },
    delivery_challan: {
        title: 'Delivery Challan Details',
        popupWidth: '1400px',
        emptyMessage: 'No delivery challans found for this customer/site in the selected period.',
        columns: [
            { key: 'delivery_challan_no', label: 'DC No' },
            { key: 'delivery_challan_date', label: 'Date' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'site_name', label: 'Site' },
            { key: 'grade', label: 'Grade' },
            { key: 'qty', label: 'Qty', align: 'right' },
            { key: 'rate', label: 'Rate', align: 'right' },
            { key: 'amount', label: 'Amount', align: 'right' },
            { key: 'delivery_mode', label: 'Mode of Delivery' },
            { key: 'distance', label: 'Distance', align: 'right' },
            { key: 'vehicle_name', label: 'Vehicle' },
            { key: 'driver_name', label: 'Driver' },
            { key: 'total_km', label: 'KM', align: 'right' },
            { key: 'marketing_name', label: 'Marketing' },
        ],
        mapDetails: (rawDetails) => rawDetails.map((item) => ({
            ...item,
            qty: formatQty(item.qty),
            rate: formatAmount(item.rate),
            amount: formatAmount(item.amount),
            distance: item.distance === '' || item.distance === null || item.distance === undefined
                ? ''
                : formatQty(item.distance),
            total_km: item.total_km === '' || item.total_km === null || item.total_km === undefined
                ? ''
                : formatQty(item.total_km),
        })),
        footerRow: (rawDetails) => ({
            delivery_challan_no: 'TOTAL',
            delivery_challan_date: '',
            customer_name: '',
            site_name: '',
            grade: '',
            qty: formatQty(rawDetails.reduce((sum, item) => sum + (Number(item.qty) || 0), 0)),
            rate: '',
            amount: formatAmount(rawDetails.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)),
            delivery_mode: '',
            distance: '',
            vehicle_name: '',
            driver_name: '',
            total_km: formatQty(rawDetails.reduce((sum, item) => sum + (Number(item.total_km) || 0), 0)),
            marketing_name: '',
        }),
    },
};

const BASE_REPORT_COLUMN_DEFS = [
    { fieldName: 'customer_name', headerName: 'Customer Name' },
    { fieldName: 'project_name', headerName: 'Project Name' },
    { fieldName: 'site_address', headerName: 'Site Address' },
    { fieldName: 'contact_person', headerName: 'Contact Person' },
    { fieldName: 'mobile_numbers', headerName: 'Mobile Nos' },
    { fieldName: 'quotation_count', headerName: 'Quotations', align: 'right', transactionType: 'quotation' },
    { fieldName: 'sales_order_count', headerName: 'Sales Orders', align: 'right', transactionType: 'sales_order' },
    { fieldName: 'delivery_challan_count', headerName: 'Delivery Challans', align: 'right', transactionType: 'delivery_challan' },
    { fieldName: 'billing_name', headerName: 'Billing Name' },
    { fieldName: 'billing_address', headerName: 'Address' },
    { fieldName: 'state', headerName: 'State' },
    { fieldName: 'pan_no', headerName: 'PAN No' },
    { fieldName: 'gst_no', headerName: 'GST No' },
    { fieldName: 'contact_details', headerName: 'Contact Details' },
    { fieldName: 'email', headerName: 'Email' },
    { fieldName: 'credit_limit', headerName: 'Credit Limit', align: 'right' },
    { fieldName: 'customer_type', headerName: 'Type of Customer' },
    { fieldName: 'nature_of_business', headerName: 'Nature of Business' },
    { fieldName: 'marketing_name', headerName: 'Marketing Name' },
];

function buildExportColumns() {
    return BASE_REPORT_COLUMN_DEFS.map(({ fieldName, headerName, align }) => ({
        fieldName,
        headerName,
        align,
    }));
}

function resolveMultiIds(selected) {
    if (!selected || selected.length === 0) {
        return '';
    }
    if (selected.some((item) => item.value === 'select-all')) {
        return '*';
    }
    return selected.map((item) => item.value).join(',');
}

function CustomerDataReport() {
    const [cookies] = useCookies(['myToken']);
    const [records, setRecords] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [salesreps, setSalesreps] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const [selectedSalesrep, setSelectedSalesrep] = useState([]);
    const [fromDate, setFromDate] = useState(moment().subtract(5, 'years').startOf('year'));
    const [toDate, setToDate] = useState(moment());
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [hasGenerated, setHasGenerated] = useState(false);

    const openTransactionDetails = useCallback((row, transactionType) => {
        const countField = {
            quotation: 'quotation_count',
            sales_order: 'sales_order_count',
            delivery_challan: 'delivery_challan_count',
        }[transactionType];
        const config = TRANSACTION_POPUP_CONFIG[transactionType];
        if (!config || !row?.customer_id) {
            return;
        }
        if (!Number(row[countField])) {
            Swal.fire(config.emptyMessage, '', 'info');
            return;
        }
        if (!fromDate?.isValid() || !toDate?.isValid()) {
            displayError({ message: 'Please select a valid date range.' }, 'Report Failed');
            return;
        }

        Swal.fire({
            title: 'Loading...',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        const params = {
            type: transactionType,
            customer_id: row.customer_id,
            from_date: fromDate.format('DD-MM-YYYY'),
            upto_date: toDate.format('DD-MM-YYYY'),
        };
        if (row.consignee_id) {
            params.consignee_id = row.consignee_id;
        }

        getCustomerDataTransactionDetails(cookies, params)
            .then((response) => {
                Swal.close();
                const rawDetails = response.details || [];
                if (!rawDetails.length) {
                    Swal.fire(config.emptyMessage, '', 'info');
                    return;
                }
                const subtitle = `${row.customer_name}${row.project_name ? ` — ${row.project_name}` : ''}`;
                const filenameBase = `${transactionType}_${sanitizeFilenamePart(row.customer_name)}_${sanitizeFilenamePart(row.project_name || 'all')}_${fromDate.format('DD-MM-YYYY')}_to_${toDate.format('DD-MM-YYYY')}`;
                showTransactionPopup({
                    title: config.title,
                    subtitle,
                    columns: config.columns,
                    details: config.mapDetails(rawDetails),
                    footerRow: config.footerRow(rawDetails),
                    emptyMessage: config.emptyMessage,
                    width: config.popupWidth || '960px',
                    exportContext: {
                        cookies,
                        filenameBase,
                        periodFrom: fromDate.format('DD-MM-YYYY'),
                        periodTo: toDate.format('DD-MM-YYYY'),
                        generatedAt: moment().format('DD-MM-YYYY HH:mm'),
                    },
                });
            })
            .catch((error) => {
                Swal.close();
                displayError(error?.response?.data || error, 'Failed to load details');
            });
    }, [cookies, fromDate, toDate]);

    const reportColumns = useMemo(() => BASE_REPORT_COLUMN_DEFS.map((column) => {
        if (!column.transactionType) {
            return column;
        }
        return {
            ...column,
            getCellStyle: clickableCellStyle,
            onCellClick: (row) => openTransactionDetails(row, column.transactionType),
        };
    }), [openTransactionDetails]);

    const exportColumns = useMemo(() => buildExportColumns(), []);

    const buildExportParams = useCallback(() => {
        const params = {
            from_date: fromDate.format('DD-MM-YYYY'),
            upto_date: toDate.format('DD-MM-YYYY'),
        };
        const salesrepIds = resolveMultiIds(selectedSalesrep);
        const customerIds = resolveMultiIds(selectedCustomer);
        if (salesrepIds) {
            params.salesrep_ids = salesrepIds;
        }
        if (customerIds) {
            params.customer_ids = customerIds;
        }
        return params;
    }, [fromDate, selectedCustomer, selectedSalesrep, toDate]);

    const buildExportMeta = useCallback((exportData) => ({
        periodFrom: exportData.period_from || fromDate.format('DD-MM-YYYY'),
        periodTo: exportData.period_to || toDate.format('DD-MM-YYYY'),
        generatedAt: moment().format('DD-MM-YYYY HH:mm'),
        rowCount: exportData.record_count || exportData.customer_data_report_list?.length || 0,
    }), [fromDate, toDate]);

    const customersWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All customers' }, ...customers],
        [customers],
    );

    const salesrepsWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All sales reps' }, ...salesreps],
        [salesreps],
    );

    useEffect(() => {
        getAllSalesRepresentatives(cookies).then((salesRepList) => {
            setSalesreps((salesRepList.salesrep_data || [])
                .filter((item) => item.status === true || item.status === 1 || item.status === 'Active')
                .map((sales) => ({ value: sales.id, label: sales.entity_name })));
        });
        getAllCustomerMain(cookies).then((customerList) => {
            setCustomers((customerList.customer_list || [])
                .filter((item) => item.status === true || item.status === 1 || item.status === 'Active')
                .map((customer) => ({ value: customer.id, label: customer.name })));
        });
    }, [cookies]);

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return records;
        }
        return records.filter((row) => BASE_REPORT_COLUMN_DEFS.some((col) => {
            const value = row[col.fieldName];
            return value !== null && value !== undefined && String(value).toLowerCase().includes(query);
        }));
    }, [records, searchText]);

    const exportFileBase = useMemo(() => {
        const datePart = fromDate?.isValid() && toDate?.isValid()
            ? `${fromDate.format('DD-MM-YYYY')}_to_${toDate.format('DD-MM-YYYY')}`
            : moment().format('DD-MM-YYYY');
        return `CustomerDataReport_${datePart}`;
    }, [fromDate, toDate]);

    const pdfSummaryLines = useMemo(() => {
        if (!fromDate?.isValid() || !toDate?.isValid()) {
            return [];
        }
        return [`Period: ${fromDate.format('DD-MM-YYYY')} to ${toDate.format('DD-MM-YYYY')}`];
    }, [fromDate, toDate]);

    const loadReport = useCallback(() => {
        if (!fromDate?.isValid() || !toDate?.isValid()) {
            displayError({ message: 'Please select a valid date range.' }, 'Report Failed');
            return Promise.resolve();
        }

        setIsLoading(true);
        const params = {
            from_date: fromDate.format('DD-MM-YYYY'),
            upto_date: toDate.format('DD-MM-YYYY'),
        };
        const salesrepIds = resolveMultiIds(selectedSalesrep);
        const customerIds = resolveMultiIds(selectedCustomer);
        if (salesrepIds) {
            params.salesrep_ids = salesrepIds;
        }
        if (customerIds) {
            params.customer_ids = customerIds;
        }

        return getCustomerDataReport(cookies, params)
            .then((response) => {
                setRecords(response.customer_data_report_list || []);
                setHasGenerated(true);
            })
            .catch((error) => {
                displayError(error?.response?.data || error, 'Report Failed');
                setRecords([]);
            })
            .finally(() => setIsLoading(false));
    }, [cookies, fromDate, selectedCustomer, selectedSalesrep, toDate]);

    const ensureExportData = () => {
        if (!hasGenerated || !filteredRecords.length) {
            displayError({ message: 'Generate the report first, then export.' }, 'Export Failed');
            return false;
        }
        return true;
    };

    const handleCSVExport = () => {
        if (!ensureExportData()) return;
        exportAsCSV(exportColumns, filteredRecords, exportFileBase);
    };

    const handleExcelExport = async () => {
        if (!ensureExportData()) return;
        setIsLoading(true);
        try {
            const exportData = await getCustomerDataReportExport(cookies, buildExportParams());
            exportCustomerDataReportExcel(exportData, exportFileBase, buildExportMeta(exportData));
        } catch (error) {
            displayError(error?.response?.data || error, 'Excel export failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePDFExport = async () => {
        if (!ensureExportData()) return;
        setIsLoading(true);
        try {
            const exportData = await getCustomerDataReportExport(cookies, buildExportParams());
            const header = await buildCustomerLedgerPdfHeader(cookies, [], [], {
                documentTitle: 'Customer Data Report',
                subtitle: pdfSummaryLines[0] || '',
            });
            const blob = createCustomerDataReportPdfBlob(exportData, header, buildExportMeta(exportData));
            openPdfInApp(blob, {
                title: 'Customer Data Report',
                downloadFileName: `${exportFileBase}.pdf`,
            });
        } catch (error) {
            displayError(error?.response?.data || error, 'PDF export failed');
        } finally {
            setIsLoading(false);
        }
    };

    const { onImage: handleImageExport } = useReportTableExports({
        columns: exportColumns,
        records: filteredRecords,
        title: 'Customer Data Report',
        filename: exportFileBase,
    });

    const exportButtons = (
        <ReportExportDropdown
            onCSV={handleCSVExport}
            onExcel={handleExcelExport}
            onPDF={handlePDFExport}
            onImage={handleImageExport}
            excelLabel="Excel (Formatted)"
            pdfLabel="PDF (Formatted)"
            disabled={isLoading || !filteredRecords.length}
        />
    );

    const toolbarFilters = (
        <>
            <div className="list-page-toolbar-filters-row">
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            styles={filterSelectStyles}
                            options={salesrepsWithSelectAll}
                            value={selectedSalesrep}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedSalesrep,
                                setSelectedSalesrep,
                                salesreps,
                                salesrepsWithSelectAll,
                            )}
                            placeholder="Sales Rep"
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            styles={filterSelectStyles}
                            options={customersWithSelectAll}
                            value={selectedCustomer}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedCustomer,
                                setSelectedCustomer,
                                customers,
                                customersWithSelectAll,
                            )}
                            placeholder="Customer"
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter-date">
                        <RangePicker
                            value={[fromDate, toDate]}
                            format="DD-MM-YYYY"
                            onChange={(values) => {
                                if (values?.[0] && values?.[1]) {
                                    setFromDate(values[0]);
                                    setToDate(values[1]);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="list-page-toolbar-filters-row list-page-toolbar-filters-row-actions">
                {exportButtons}
                <Button
                    color="primary"
                    className="list-page-toolbar-generate-btn"
                    onClick={loadReport}
                    disabled={isLoading}
                >
                    Generate
                </Button>
            </div>
        </>
    );

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap" className="customer-data-report-page">
                <div className="customer-data-report-shell">
                    <ListPageLayout
                        moduleLabel="REPORTS"
                        title="Customer Data Report"
                        subtitle="Customer billing details with project/site information. Click Quotations, Sales Orders, or Delivery Challans to view details."
                        modeLabel="REPORT"
                        recordCount={filteredRecords.length}
                        recordLabel="rows"
                        searchText={searchText}
                        onSearchChange={setSearchText}
                        searchPlaceholder="Search customer, project, site, GST, mobile..."
                        toolbarFilters={toolbarFilters}
                        toolbarVariant="stacked"
                        reportListLink="/ReportAll"
                    >
                        <ReactTableWrapper
                            columns={reportColumns}
                            data={filteredRecords}
                            exportFilename={exportFileBase}
                            isLoading={isLoading}
                            enableTableSearch={false}
                            compactRows
                            layoutFullWidth
                            defaultPageSize={10}
                        />
                    </ListPageLayout>
                </div>
            </div>
        </div>
    );
}

export default CustomerDataReport;
